import React, { useEffect, useMemo, useState } from "react";
import Select, { SingleValue } from "react-select";
import { Bookmark, CornerDownRight, FileText, Loader2, Search, X } from "lucide-react";
import { useAuth } from "../../../firebase/AuthProvider";
import UserInfo from "../../../interfaces/UserInfo";
import {
    Conversation,
    getConversationMessageAuthors,
    Message,
    searchConversationMessages,
} from "../../../services/chatService/ChatService";
import MilestoneAvatar from "../../ui/Avatar/MilestoneAvatar";
import "../formFillComponents/DynamicFormComponents/FormInput.css";

type PanelMode = "search" | "bookmarks";
type AuthorOption = { value: string; label: string };

interface ChatSearchPanelProps {
    conversation: Conversation;
    mode: PanelMode;
    onClose: () => void;
    onOpenResult: (message: Message) => void;
    onToggleBookmark: (messageId: number) => Promise<boolean>;
    appearance?: "default" | "meeting-dark";
}

const senderLabel = (sender?: UserInfo | null) => {
    if (!sender) return "Unknown sender";
    return sender.displayName || [sender.firstName, sender.lastName].filter(Boolean).join(" ") || sender.email || "Unknown sender";
};

const resultText = (message: Message) => {
    const text = message.messagePlaintext || message.messageContent || "";
    return text.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim() || (message.attachmentUrl ? "Shared a file" : "Message");
};

const ChatSearchPanel: React.FC<ChatSearchPanelProps> = ({
    conversation,
    mode,
    onClose,
    onOpenResult,
    onToggleBookmark,
    appearance = "default",
}) => {
    const { currentUser } = useAuth();
    const isDark = appearance === "meeting-dark";
    const [query, setQuery] = useState("");
    const [authorId, setAuthorId] = useState("");
    const [filesOnly, setFilesOnly] = useState(false);
    const [includeThreads, setIncludeThreads] = useState(true);
    const [authors, setAuthors] = useState<UserInfo[]>([]);
    const [results, setResults] = useState<Message[]>([]);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState("");
    const [hasMore, setHasMore] = useState(false);
    const pageSize = 20;

    const canSearch = mode === "bookmarks" || query.trim().length >= 2 || Boolean(authorId) || filesOnly;
    const title = mode === "bookmarks" ? "Saved messages" : "Find in conversation";

    useEffect(() => {
        let cancelled = false;
        const loadAuthors = async () => {
            if (!currentUser) return;
            try {
                const data = await getConversationMessageAuthors(await currentUser.getIdToken(), conversation.id);
                if (!cancelled) setAuthors(data);
            } catch (loadError) {
                console.error("Unable to load conversation message authors", loadError);
            }
        };
        void loadAuthors();
        return () => { cancelled = true; };
    }, [conversation.id, currentUser]);

    useEffect(() => {
        setResults([]);
        setPage(1);
        setHasMore(false);
        if (!canSearch || !currentUser) {
            setLoading(false);
            return;
        }

        let cancelled = false;
        const timer = window.setTimeout(async () => {
            setLoading(true);
            setError("");
            try {
                const data = await searchConversationMessages(await currentUser.getIdToken(), conversation.id, {
                    query: query.trim() || undefined,
                    authorId: authorId || undefined,
                    filesOnly,
                    bookmarkedOnly: mode === "bookmarks",
                    includeThreads,
                    pageIndex: 1,
                    pageSize,
                });
                if (!cancelled) {
                    setResults(data);
                    setHasMore(data.length === pageSize);
                }
            } catch (searchError: any) {
                if (!cancelled) setError(searchError?.message || "Search failed.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, mode === "search" ? 300 : 100);

        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [authorId, canSearch, conversation.id, currentUser, filesOnly, includeThreads, mode, query]);

    const loadMore = async () => {
        if (!currentUser || loadingMore) return;
        const nextPage = page + 1;
        setLoadingMore(true);
        try {
            const data = await searchConversationMessages(await currentUser.getIdToken(), conversation.id, {
                query: query.trim() || undefined,
                authorId: authorId || undefined,
                filesOnly,
                bookmarkedOnly: mode === "bookmarks",
                includeThreads,
                pageIndex: nextPage,
                pageSize,
            });
            setResults(previous => [...previous, ...data.filter(item => !previous.some(existing => existing.id === item.id))]);
            setPage(nextPage);
            setHasMore(data.length === pageSize);
        } catch (loadError: any) {
            setError(loadError?.message || "Unable to load more results.");
        } finally {
            setLoadingMore(false);
        }
    };

    const emptyCopy = useMemo(() => {
        if (!canSearch) return "Enter at least two characters, or choose a sender or file filter.";
        if (mode === "bookmarks") return "No saved messages match these filters.";
        return "No messages match your search.";
    }, [canSearch, mode]);

    const authorOptions = useMemo<AuthorOption[]>(() => [
        { value: "", label: "Anyone" },
        ...authors.map(author => ({ value: author.id, label: senderLabel(author) })),
    ], [authors]);
    const selectedAuthor = authorOptions.find(option => option.value === authorId) ?? authorOptions[0];

    const handleBookmark = async (messageId: number) => {
        try {
            const isBookmarked = await onToggleBookmark(messageId);
            if (mode === "bookmarks" && !isBookmarked) {
                setResults(previous => previous.filter(message => message.id !== messageId));
            } else {
                setResults(previous => previous.map(message => message.id === messageId ? { ...message, isBookmarked } : message));
            }
        } catch {
            // The shared handler owns user-facing errors.
        }
    };

    return (
        <section className={`chat-search-panel dark-page-canvas flex h-full w-full flex-col ${isDark ? "bg-slate-950 text-slate-100" : "bg-white text-slate-900 dark:bg-[#11131c] dark:text-slate-100"}`} aria-label={title}>
            <header className={`dark-surface flex min-h-[52px] shrink-0 items-center justify-between px-3 ${isDark ? "bg-slate-900" : "bg-slate-50 dark:bg-[#171a24]"}`}>
                <div className="flex items-center gap-2.5">
                    {mode === "bookmarks" ? <Bookmark size={18} className="text-[#7E57C2]" /> : <Search size={18} className="text-[#7E57C2]" />}
                    <div>
                        <h3 className="m-0 text-sm font-semibold">{title}</h3>
                        <p className={`m-0 text-[10px] ${isDark ? "text-slate-400" : "text-slate-500"}`}>Only messages from this conversation</p>
                    </div>
                </div>
                <button type="button" onClick={onClose} aria-label={`Close ${title}`} className={`rounded-lg p-2 ${isDark ? "text-slate-400 hover:bg-slate-800" : "text-slate-500 hover:bg-slate-200"}`}>
                    <X size={18} />
                </button>
            </header>

            <div className={`dark-surface shrink-0 space-y-2 p-3 ${isDark ? "bg-slate-900/60" : "bg-white dark:bg-[#171a24]"}`}>
                <label className={`chat-search-field flex items-center gap-2 rounded-xl px-3 ${isDark ? "bg-slate-900" : "bg-slate-100 dark:bg-[#1d202b]"}`}>
                    <Search size={16} className="shrink-0 text-slate-400" />
                    <input
                        autoFocus
                        value={query}
                        onChange={event => setQuery(event.target.value)}
                        placeholder={mode === "bookmarks" ? "Search saved messages" : "Search messages or filenames"}
                        className={`chat-search-input h-10 min-w-0 flex-1 border-0 bg-transparent text-sm outline-none ring-0 shadow-none ${isDark ? "text-slate-100 placeholder:text-slate-500" : "text-slate-800 placeholder:text-slate-400 dark:text-slate-100 dark:placeholder:text-slate-500"}`}
                    />
                    {query && <button type="button" onClick={() => setQuery("")} aria-label="Clear search"><X size={14} /></button>}
                </label>

                <div className="flex flex-wrap items-center gap-2">
                    <Select<AuthorOption, false>
                        value={selectedAuthor}
                        options={authorOptions}
                        onChange={(option: SingleValue<AuthorOption>) => setAuthorId(option?.value ?? "")}
                        aria-label="Filter by sender"
                        className="form-v2-react-select react-select-container react_slect_sm chat-author-select min-w-[120px] max-w-[190px] text-xs"
                        classNamePrefix="react-select"
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        isSearchable={authors.length > 5}
                    />
                    <label className={`chat-filter-button flex h-8 cursor-pointer items-center gap-1.5 rounded-lg px-2.5 text-xs ${filesOnly ? "chat-filter-button-active bg-purple-50 text-[#6847A3]" : isDark ? "text-slate-300" : "text-slate-600"}`}>
                        <input type="checkbox" checked={filesOnly} onChange={event => setFilesOnly(event.target.checked)} className="sr-only" />
                        <FileText size={14} /> Files only
                    </label>
                    <label className={`chat-filter-button flex h-8 cursor-pointer items-center gap-1.5 rounded-lg px-2.5 text-xs ${includeThreads ? "chat-filter-button-active bg-purple-50 text-[#6847A3]" : isDark ? "text-slate-300" : "text-slate-600"}`}>
                        <input type="checkbox" checked={includeThreads} onChange={event => setIncludeThreads(event.target.checked)} className="sr-only" />
                        <CornerDownRight size={14} /> Threads
                    </label>
                </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto custom-scrollbar p-2">
                {loading ? (
                    <div className="flex h-32 items-center justify-center"><Loader2 className="animate-spin text-[#7E57C2]" size={24} /></div>
                ) : error ? (
                    <p className="m-4 rounded-xl bg-red-50 p-3 text-center text-xs text-red-700">{error}</p>
                ) : results.length === 0 ? (
                    <p className={`chat-search-empty m-8 text-center text-xs leading-5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>{emptyCopy}</p>
                ) : (
                    <div className="space-y-1.5">
                        {results.map(message => (
                            <article key={message.id} className={`dark-surface group flex gap-2.5 rounded-xl p-3 transition ${isDark ? "bg-slate-900 hover:bg-slate-800" : "bg-white hover:bg-purple-50/30 dark:bg-[#171a24] dark:hover:bg-[#1d202b]"}`}>
                                <MilestoneAvatar size="xxs" userId={message.author?.id || ""} displayName={senderLabel(message.author)} alternate />
                                <button type="button" onClick={() => onOpenResult(message)} className="min-w-0 flex-1 border-0 bg-transparent p-0 text-left">
                                    <div className="flex items-center gap-2">
                                        <span className="chat-search-result-author truncate text-xs font-semibold">{senderLabel(message.author)}</span>
                                        <time className={`chat-search-result-time shrink-0 text-[10px] ${isDark ? "text-slate-500" : "text-slate-400"}`}>{new Date(message.createDate).toLocaleString()}</time>
                                    </div>
                                    <p className={`chat-search-result-text mb-0 mt-1 line-clamp-2 text-xs leading-5 ${isDark ? "text-slate-300" : "text-slate-600"}`}>{resultText(message)}</p>
                                    <div className="mt-1.5 flex items-center gap-2 text-[10px] text-[#7E57C2]">
                                        {message.attachmentUrl && <span className="flex items-center gap-1"><FileText size={11} /> File</span>}
                                        {message.threadRootMessageId && <span className="flex items-center gap-1"><CornerDownRight size={11} /> Thread reply</span>}
                                    </div>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => void handleBookmark(message.id)}
                                    aria-label={message.isBookmarked ? "Remove saved message" : "Save message"}
                                    className={`chat-bookmark-button h-8 w-8 shrink-0 rounded-lg transition ${message.isBookmarked ? "chat-bookmark-button-active bg-purple-100 text-[#6847A3]" : isDark ? "text-slate-500 hover:bg-slate-800 hover:text-purple-300" : "text-slate-400 hover:bg-slate-100 hover:text-[#7E57C2]"}`}
                                >
                                    <Bookmark size={15} className="mx-auto" fill={message.isBookmarked ? "currentColor" : "none"} />
                                </button>
                            </article>
                        ))}
                        {hasMore && (
                            <button type="button" onClick={() => void loadMore()} disabled={loadingMore} className={`flex h-9 w-full items-center justify-center gap-2 rounded-lg text-xs font-semibold ${isDark ? "text-purple-300 hover:bg-slate-900" : "text-[#6847A3] hover:bg-purple-50"}`}>
                                {loadingMore && <Loader2 size={14} className="animate-spin" />} Load more
                            </button>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
};

export default ChatSearchPanel;
